/**
 * Reads animation timing out of an incoming .pptx.
 *
 * pptx-parser ignores <p:timing> entirely, so the file is opened as a zip alongside the
 * normal import and each slide's timing is parsed here.
 *
 * Correlation note: effects reference shapes by the source file's shape id. The importer
 * builds its own objects with fresh ids, so mapping an effect onto an imported object needs
 * the source shape order, which `shapeOrder` provides — shapes appear in <p:spTree> in the
 * same order pptx-parser reports them as pageElements.
 */

import JSZip from 'jszip';
import { parseTimingXml } from './pptxAnimationXml';

/** Slide parts sorted by their numeric suffix, since zip order is not guaranteed. */
const slidePartNames = (zip) =>
  Object.keys(zip.files)
    .filter((name) => /^ppt\/slides\/slide\d+\.xml$/.test(name))
    .sort((a, b) => {
      const num = (s) => Number(s.match(/slide(\d+)\.xml$/)[1]);
      return num(a) - num(b);
    });

/**
 * Shape ids in document order, so an effect's spid can be resolved to the index of the
 * element the importer created from that same shape.
 */
export const shapeOrderFromXml = (slideXml) => {
  const ids = [];
  const treeMatch = slideXml.match(/<p:spTree[\s\S]*?<\/p:spTree>/);
  if (!treeMatch) return ids;
  const pattern = /<p:cNvPr\b[^>]*\bid="(\d+)"/g;
  let match = pattern.exec(treeMatch[0]);
  while (match) {
    ids.push(match[1]);
    match = pattern.exec(treeMatch[0]);
  }
  // The first cNvPr in a spTree belongs to the tree's own non-visual properties, not a shape.
  return ids.slice(1);
};

/**
 * @param {File|Blob} file  the .pptx being imported
 * @returns {Promise<Array<{ steps: Array, passthrough: string[], shapeOrder: string[] }>>}
 *          one entry per slide, in slide order
 */
export const extractAnimations = async (file) => {
  try {
    const zip = await JSZip.loadAsync(file);
    const names = slidePartNames(zip);
    const results = [];

    for (const name of names) {
      // eslint-disable-next-line no-await-in-loop
      const xml = await zip.file(name).async('string');
      const { steps, passthrough } = parseTimingXml(xml);
      results.push({ steps, passthrough, shapeOrder: shapeOrderFromXml(xml) });
    }
    return results;
  } catch (err) {
    // A deck we can't read for animation is still a deck we can import.
    console.error('Could not read animation timing from the .pptx:', err);
    return [];
  }
};

/** Total number of effects found across all slides, for the import summary message. */
export const countAnimations = (perSlide = []) =>
  perSlide.reduce((sum, s) => sum + s.steps.length + s.passthrough.length, 0);

/**
 * Turn one slide's parsed timing into an animations container for the imported slide.
 *
 * `elementOrder[k]` is the object the importer built from the k-th shape of the source
 * slide, and `shapeOrder[k]` is that shape's id — so a step's `sourceSpid` resolves to an
 * object through the shared index.
 *
 * Effects whose shape produced no importable object (a group, a chart, a diagram) are
 * dropped rather than guessed at, and reported through the returned `unmapped` count.
 *
 * Passthrough XML is deliberately NOT carried over: it references shape ids from the source
 * file, and export assigns entirely new ones, so re-emitting it would point at shapes that
 * do not exist. Preserving it properly needs the importer to retain source shape identity.
 *
 * @returns {{ animations: { steps, passthrough }, unmapped: number }}
 */
export const mapAnimationsToSlide = (parsedSlide, elementOrder = [], makeStep) => {
  const result = { animations: { steps: [], passthrough: [] }, unmapped: 0 };
  if (!parsedSlide) return result;

  const indexBySpid = new Map();
  (parsedSlide.shapeOrder || []).forEach((spid, index) => {
    if (!indexBySpid.has(spid)) indexBySpid.set(spid, index);
  });

  (parsedSlide.steps || []).forEach((step) => {
    const index = indexBySpid.get(step.sourceSpid);
    const element = index === undefined ? null : elementOrder[index];
    if (!element) {
      result.unmapped += 1;
      return;
    }
    result.animations.steps.push(
      makeStep({
        targetType: element.type,
        targetId: element.id,
        effect: step.effect,
        direction: step.direction,
        trigger: step.trigger,
        duration: step.duration,
        delay: step.delay,
      })
    );
  });

  result.unmapped += (parsedSlide.passthrough || []).length;
  return result;
};

export default extractAnimations;
